import React, { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  Alert,
  LinearProgress,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { MdCloudUpload, MdOutlineFileDownload } from 'react-icons/md';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMain } from '../../hooks/useMain';

const norm = (s) => (s || '').toString().trim().toLowerCase();

const TEMPLATE_HEADERS = [
  'date',
  'typeOfExpense',
  'amountPaid',
  'modeOfPayment',
  'recipientName',
  'description',
];

const getField = (row, key) => {
  if (row[key] !== undefined) return row[key];
  const rowKeys = Object.keys(row);
  const match = rowKeys.find((k) => norm(k) === norm(key));
  return match !== undefined ? row[match] : undefined;
};

// duplicate = same date + type + recipient + amount
const duplicateKey = (r) => `${norm(r.date)}|${norm(r.typeOfExpense)}|${norm(r.recipientName)}|${norm(r.amountPaid)}`;

const excelSerialDateToJSDate = (serial) => {
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);
  const fractional_day = serial - Math.floor(serial) + 0.0000001;
  let total_seconds = Math.floor(86400 * fractional_day);
  const seconds = total_seconds % 60;
  total_seconds -= seconds;
  const minutes = (total_seconds / 60) % 60;
  total_seconds -= minutes * 60;
  const hours = total_seconds / 3600;
  return new Date(date_info.getFullYear(), date_info.getMonth(), date_info.getDate(), hours, minutes, seconds);
};

const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseExcelDateToString = (rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === '') return '';
  if (rawValue instanceof Date && !isNaN(rawValue.getTime())) return formatDateLocal(rawValue);
  if (typeof rawValue === 'number' && !isNaN(rawValue)) return formatDateLocal(excelSerialDateToJSDate(rawValue));
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (/^\d+(\.\d+)?$/.test(trimmed)) return formatDateLocal(excelSerialDateToJSDate(Number(trimmed)));
    let match = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    match = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) return formatDateLocal(parsed);
    return '';
  }
  return '';
};

const BulkUploadExpenseDialog = ({ onUploaded }) => {
  const { activeBranchId, createManyExpense, setShowMessage } = useMain();

  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFileName('');
    setParsedRows([]);
    setParseError('');
    setSelectedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetState();
  };

  const downloadExcelTemplate = () => {
    const sampleData = [
      {
        date: '2026-08-18',
        typeOfExpense: 'Groceries',
        amountPaid: 500,
        modeOfPayment: 'Cash',
        recipientName: 'Supermarket',
        description: 'Weekly groceries',
      },
      {
        date: '2026-08-19',
        typeOfExpense: 'Salary',
        amountPaid: 15000,
        modeOfPayment: 'PhonePe',
        recipientName: 'Staff Name',
        description: 'Monthly salary',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData, { header: TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
    XLSX.writeFile(wb, 'expense_template.xlsx');
  };

  const buildDuplicateFlags = (rows) => {
    const counts = new Map();
    rows.forEach((r) => {
      if (r.invalid) return;
      const key = duplicateKey(r);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return rows.map((r) => ({
      ...r,
      isDuplicate: !r.invalid && counts.get(duplicateKey(r)) > 1,
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setSelectedRows([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!jsonData.length) {
          setParseError('The file has no data rows.');
          setParsedRows([]);
          return;
        }

        let mapped = jsonData.map((item, idx) => {
          const date = parseExcelDateToString(getField(item, 'date'));
          const typeOfExpense = (getField(item, 'typeOfExpense') || '').toString().trim();
          const amountPaidRaw = getField(item, 'amountPaid');
          const amountPaid = amountPaidRaw === '' ? '' : Number(amountPaidRaw);
          const modeOfPayment = (getField(item, 'modeOfPayment') || '').toString().trim();
          const recipientName = (getField(item, 'recipientName') || '').toString().trim();
          const description = (getField(item, 'description') || '').toString().trim();

          const row = {
            rowIndex: idx + 2,
            date,
            typeOfExpense,
            amountPaid: isNaN(amountPaid) ? '' : amountPaid,
            modeOfPayment,
            recipientName,
            description,
          };

          const missingFields = [];
          if (!row.date) missingFields.push('date');
          if (row.amountPaid === '') missingFields.push('amountPaid');

          row.invalid = missingFields.length > 0;
          row.invalidReason = missingFields.length ? `Missing/invalid: ${missingFields.join(', ')}` : '';
          return row;
        });

        mapped = buildDuplicateFlags(mapped);
        setParsedRows(mapped);
      } catch (err) {
        setParseError('Could not read this file. Please upload a valid .xlsx or .xls file.');
        setParsedRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRemoveRow = (rowIndex) => {
    setParsedRows((prev) => buildDuplicateFlags(prev.filter((r) => r.rowIndex !== rowIndex)));
    setSelectedRows((prev) => prev.filter((i) => i !== rowIndex));
  };

  const handleRemoveAllDuplicates = () => {
    const seen = new Set();
    const deduped = [];
    parsedRows.forEach((r) => {
      if (r.invalid) {
        deduped.push(r);
        return;
      }
      const key = duplicateKey(r);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(r);
    });
    const removedCount = parsedRows.length - deduped.length;
    setParsedRows(buildDuplicateFlags(deduped));
    setSelectedRows((prev) => prev.filter((i) => deduped.some((r) => r.rowIndex === i)));
    setShowMessage?.({
      message: removedCount > 0 ? `Removed ${removedCount} duplicate row(s)` : 'No duplicates found',
      messageType: removedCount > 0 ? 'success' : 'error',
    });
  };

  const selectableRows = parsedRows.filter((r) => !r.invalid);
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedRows(allSelected ? [] : selectableRows.map((r) => r.rowIndex));
  };

  const toggleSelectRow = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex]
    );
  };

  const invalidCount = parsedRows.filter((r) => r.invalid).length;
  const duplicateCount = parsedRows.filter((r) => r.isDuplicate).length;
  const readyCount = selectableRows.length;

  const handleSubmit = async () => {
    if (parsedRows.length === 0) {
      setShowMessage({ message: 'Upload a valid file', messageType: 'error' });
      return;
    }
    if (selectedRows.length === 0) {
      setShowMessage({ message: 'Select at least one row to upload', messageType: 'error' });
      return;
    }

    const rowsToUpload = parsedRows
      .filter((r) => selectedRows.includes(r.rowIndex))
      .map((r) => ({
        date: r.date,
        typeOfExpense: r.typeOfExpense,
        amountPaid: r.amountPaid,
        modeOfPayment: r.modeOfPayment,
        recipientName: r.recipientName,
        description: r.description,
      }));

    setSubmitting(true);
    try {
      const res = await createManyExpense(activeBranchId, { bulkData: rowsToUpload });
      if (res?.statusCode === 200) {
        setShowMessage({ message: res.message || 'Uploaded successfully', messageType: 'success' });
        handleClose();
        onUploaded?.();
      } else {
        setShowMessage({ message: res?.message || 'Upload failed', messageType: 'error' });
      }
    } catch (err) {
      setShowMessage({ message: err.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<MdCloudUpload />}
        sx={{ background: '#6174DD', height: '40px', minWidth: '140px', '&:hover': { background: '#4f5fc7' } }}
        onClick={handleOpen}
      >
        Bulk Upload
      </Button>

      <Dialog
        open={open}
        onClose={submitting ? undefined : handleClose}
        maxWidth={parsedRows.length > 0 ? 'lg' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          Bulk Upload Expenses{' '}
          <Tooltip
            title="Upload an excel file with columns: date, typeOfExpense, amountPaid, modeOfPayment, recipientName, description. Preview and fix the data below before uploading — duplicate rows (same date + type + recipient + amount) are flagged automatically and can be removed."
            placement="right"
            arrow
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" disabled={submitting}>
              Choose File
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileSelect} />
            </Button>
            <Button
              variant="outlined"
              startIcon={<MdOutlineFileDownload />}
              onClick={downloadExcelTemplate}
              disabled={submitting}
            >
              Download Sample File
            </Button>
            {fileName && <Typography variant="body2">{fileName}</Typography>}
          </Box>

          {submitting && <LinearProgress sx={{ mb: 2 }} />}

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {parsedRows.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <Chip label={`${parsedRows.length} rows total`} />
                <Chip label={`${readyCount} valid`} color="success" />
                {invalidCount > 0 && <Chip label={`${invalidCount} invalid`} color="error" />}
                {duplicateCount > 0 && <Chip label={`${duplicateCount} duplicate rows`} color="warning" />}
                <Chip label={`${selectedRows.length} selected`} color="primary" variant="outlined" />
                {duplicateCount > 0 && (
                  <Button size="small" color="warning" variant="outlined" onClick={handleRemoveAllDuplicates}>
                    Remove duplicates (keep first)
                  </Button>
                )}
              </Box>

              <Box sx={{ maxHeight: '440px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleSelectAll}
                          disabled={selectableRows.length === 0}
                        />
                      </TableCell>
                      <TableCell>Row</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Mode</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Remove</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.map((r) => (
                      <TableRow
                        key={r.rowIndex}
                        selected={selectedRows.includes(r.rowIndex)}
                        sx={{
                          backgroundColor: r.invalid ? '#ffebee' : r.isDuplicate ? '#fff3e0' : 'inherit',
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(r.rowIndex)}
                            onChange={() => toggleSelectRow(r.rowIndex)}
                            disabled={r.invalid}
                          />
                        </TableCell>
                        <TableCell>{r.rowIndex}</TableCell>
                        <TableCell>{r.date || '—'}</TableCell>
                        <TableCell>{r.typeOfExpense || '—'}</TableCell>
                        <TableCell>{r.amountPaid === '' ? '—' : r.amountPaid}</TableCell>
                        <TableCell>{r.modeOfPayment || '—'}</TableCell>
                        <TableCell>{r.recipientName || '—'}</TableCell>
                        <TableCell>{r.description || '—'}</TableCell>
                        <TableCell>
                          {r.invalid ? (
                            <Tooltip title={r.invalidReason || 'Invalid row'}>
                              <Chip size="small" label="Invalid" color="error" />
                            </Tooltip>
                          ) : r.isDuplicate ? (
                            <Chip size="small" label="Duplicate" color="warning" />
                          ) : (
                            <Chip size="small" label="Ready" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color={r.invalid ? 'error' : 'text.secondary'}>
                            {r.invalid ? r.invalidReason : r.isDuplicate ? 'Same date+type+recipient+amount as another row' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove row">
                            <IconButton size="small" onClick={() => handleRemoveRow(r.rowIndex)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: '#6174DD' }}
            onClick={handleSubmit}
            disabled={submitting || selectedRows.length === 0 || !!parseError}
          >
            {submitting ? 'Uploading...' : `Upload ${selectedRows.length || ''} Selected Expense(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadExpenseDialog;